import React from 'react';
import useControllableState from '../../utils/useControllableState';

interface PopoverContext {
  popoverId?: string;
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  triggerRef: React.RefObject<HTMLElement>;
  popoverRef: React.RefObject<HTMLElement>;
}

export interface PopoverProps {
  /**
   * Whether the popover should be open. Defining this signifies to the component that you're going to be controlling
   * its state, so you're expected to also provide an `onChange` prop in order to keep your controlled state updated
   */
  isOpen?: boolean;

  /**
   * The initial state of the popover.
   */
  initialIsOpen?: boolean;

  /**
   * Handler that gets fired whenever the popover should close or open. This is only required when you're controlling the
   * state.
   */
  onChange?: (isOpen: boolean) => void;

  /**
   * The id of the popover. Helps setting up aria controls between popover content & trigger
   */
  id?: string;

  /**
   * @ignore
   */
  children:
    | React.ReactNode
    | ((
        renderProps: Pick<PopoverContext, 'isOpen' | 'open' | 'close' | 'toggle'>,
      ) => React.ReactNode);
}

const PopoverContext = React.createContext<PopoverContext | undefined>(undefined);
const usePopoverContext = () => React.useContext(PopoverContext);

/**
 * PounceV2 Popover component built on top of MUI Popover.
 *
 * Popover is a non-modal dialog that floats around a trigger. It's used to display contextual information to the user,
 * and should be paired with a clickable trigger element. A popover is comprised of 3 required elements:
 *
 * - A `Popover`
 * - A `PopoverTrigger`
 * - A `PopoverContent`
 *
 * Maintains the same API as the original pounce Popover component while leveraging
 * MUI's accessibility and interaction patterns under the hood.
 */
export const Popover: React.FC<PopoverProps> = ({
  isOpen: controlledIsOpen,
  onChange: controlledOnChange,
  initialIsOpen = false,
  id,
  children,
}) => {
  const [isOpen, setIsOpen] = useControllableState({
    value: controlledIsOpen,
    onChange: controlledOnChange,
    defaultValue: initialIsOpen,
  });

  const triggerRef = React.useRef(null);
  const popoverRef = React.useRef(null);
  const popoverId = React.useId();
  const finalPopoverId = id || popoverId;

  const open = React.useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);
  const close = React.useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const toggle = React.useCallback(() => setIsOpen(!isOpen), [isOpen, setIsOpen]);

  const contextValue = React.useMemo(
    () => ({
      popoverId: finalPopoverId,
      triggerRef,
      popoverRef,
      open,
      close,
      toggle,
      isOpen,
    }),
    [finalPopoverId, open, close, toggle, isOpen],
  );

  return (
    <PopoverContext.Provider value={contextValue}>
      {typeof children === 'function' ? children({ isOpen, open, close, toggle }) : children}
    </PopoverContext.Provider>
  );
};

export { usePopoverContext };
